import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import apiClient from '../../lib/axios';

interface MedicationRequestDialogProps {
  open: boolean;
  onClose: () => void;
  medicationName: string;
  pharmacyProductId?: string;
  onSuccess?: () => void;
}

export const MedicationRequestDialog: React.FC<MedicationRequestDialogProps> = ({
  open,
  onClose,
  medicationName,
  pharmacyProductId,
  onSuccess,
}) => {
  const [name, setName] = useState(medicationName);
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: async () => {
      await apiClient.post('/medication-requests', {
        medicationName: name,
        note: note || undefined,
        pharmacyProductId,
      });
    },
    onSuccess: () => {
      setNote('');
      onClose();
      onSuccess?.();
    },
  });

  React.useEffect(() => {
    if (open) {
      setName(medicationName);
      setNote('');
    }
  }, [open, medicationName]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
        Solicitar Medicamento
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: '#A0AEC0', mb: 2 }}>
          {pharmacyProductId
            ? 'Solicitar reabastecimiento de este producto'
            : 'Solicitar que se agregue este medicamento al catálogo'}
        </Typography>
        <TextField
          fullWidth
          label="Medicamento"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Nota (opcional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          multiline
          rows={3}
          placeholder="Información adicional sobre la solicitud..."
        />
        {mutation.isError && (
          <Typography sx={{ fontSize: 12, color: '#E53935', mt: 1 }}>
            Error al enviar la solicitud. Intenta de nuevo.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#657786' }}>
          Cancelar
        </Button>
        <Button
          onClick={() => mutation.mutate()}
          variant="contained"
          disabled={!name.trim() || mutation.isPending}
          sx={{
            textTransform: 'none',
            background: '#0066CC',
            '&:hover': { background: '#004C99' },
          }}
        >
          {mutation.isPending ? <CircularProgress size={20} color="inherit" /> : 'Enviar Solicitud'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
